<template>
  <b-row>
    <div class="col-12 col-lg-9">
      <b-card class="mb-3" :header="heading" no-body>
        <div class="card-body">
          <b-row>
            <b-col cols="12" md="6" v-if="itemForm.id && itemForm.status == 'ToReview'">
              <b-form-group label="Status:">
                <b-v-select
                  id="form-input-status"
                  name="form-input-status"
                  v-validate="{ required: true }"
                  :state="validateState('form-input-status')"
                  v-model="itemForm.status"
                  :searchable="true"
                  :options="statusOptions"
                />
                <b-form-invalid-feedback id="form-input-status-feedback">
                  {{ veeErrors.first('form-input-status') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Source:">
                <b-v-select
                  id="form-input-source"
                  name="form-input-source"
                  v-validate="{ required: true }"
                  :state="validateState('form-input-source')"
                  v-model="itemForm.source"
                  :searchable="true"
                  :options="sourceOptions"
                  @input="onSourceChange"
                />
                <b-form-invalid-feedback id="form-input-source-feedback">
                  {{ veeErrors.first('form-input-source') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          
          <!-- UPS Source Fields -->
          <b-row v-if="itemForm.source === 'UPSDirect'">
            <b-col cols="12" md="6">
              <b-form-group label="Country:">
                <b-form-input
                  id="form-input-country"
                  name="form-input-country"
                  v-model="itemForm.country"
                  v-validate="{ required: false, max: 8 }"
                  :state="validateState('form-input-country')"
                  data-vv-as="Country"
                  placeholder="Enter country code (e.g., US, CA)"
                />
                <b-form-invalid-feedback id="form-input-country-feedback">
                  {{ veeErrors.first('form-input-country') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Charge Class Code:">
                <b-form-input
                  id="form-input-chargeClassCode"
                  name="form-input-chargeClassCode"
                  v-model="itemForm.chargeClassCode"
                  v-validate="{ required: false, max: 20 }"
                  :state="validateState('form-input-chargeClassCode')"
                  data-vv-as="Charge Class Code"
                  placeholder="Enter charge class code"
                />
                <b-form-invalid-feedback id="form-input-chargeClassCode-feedback">
                  {{ veeErrors.first('form-input-chargeClassCode') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Common Fields (Required for both UPS and DHL) -->
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Charge Description Code:">
                <b-form-input
                  id="form-input-chargeDescriptionCode"
                  name="form-input-chargeDescriptionCode"
                  v-model="itemForm.chargeDescriptionCode"
                  v-validate="{ required: false, max: 20 }"
                  :state="validateState('form-input-chargeDescriptionCode')"
                  data-vv-as="Charge Description Code"
                  placeholder="Enter charge description code"
                />
                <b-form-invalid-feedback id="form-input-chargeDescriptionCode-feedback">
                  {{ veeErrors.first('form-input-chargeDescriptionCode') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Charge Description:">
                <b-form-input
                  id="form-input-chargeDescription"
                  name="form-input-chargeDescription"
                  v-model="itemForm.chargeDescription"
                  v-validate="{ required: false, max: 150 }"
                  :state="validateState('form-input-chargeDescription')"
                  data-vv-as="Charge Description"
                  placeholder="Enter charge description"
                />
                <b-form-invalid-feedback id="form-input-chargeDescription-feedback">
                  {{ veeErrors.first('form-input-chargeDescription') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Billing Entry Type:">
                <b-v-select
                  id="form-input-type"
                  name="form-input-type"
                  v-validate="{ required: true }"
                  :state="validateState('form-input-type')"
                  v-model="itemForm.type"
                  :searchable="true"
                  :options="typeOptions"
                />
                <b-form-invalid-feedback id="form-input-type-feedback">
                  {{ veeErrors.first('form-input-type') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

        </div>
      </b-card>
    </div>
    <aside class="col-12 col-lg-3">
      <b-card header="Actions">
        <b-button
          :disabled="savingItem"
          variant="primary"
          block
          type="submit"
          @click="SaveForm"
        >
          <span v-if="savingItem">Saving...</span>
          <span v-else>Save Changes</span>
        </b-button>
        <router-link
          :disabled="savingItem"
          class="mt-2 btn btn-block btn-outline-danger"
          :to="{ name: 'Billing.CarrierInvoiceChargeConfigurations.Index' }"
        >
          <span>Cancel</span>
        </router-link>
      </b-card>
    </aside>
  </b-row>
</template>

<script>
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  mixins: [crudMixinGen('billing/carrierInvoiceChargeConfigurations'), myCommonDataMixin],
  props: {
    id: {
      type: String,
      default: '',
      required: false
    }
  },

  data: () => ({
    heading: 'Carrier Invoice: Charge Classification Form',
    statusOptions: [
      { value: 'Ready', text: 'Ready' },
      { value: 'ToReview', text: 'Needs Review' }
    ],
    sourceOptions: [
      { value: 'UPSDirect', text: 'UPS Direct' },
      { value: 'DHL', text: 'DHL' }
    ],
    typeOptions: [
      { value: 'Unknown', text: 'Unknown' },
      { value: 'FuelSurcharge', text: 'Fuel Surcharge' },
      { value: 'FreightCharge', text: 'Freight Charge' },
      { value: 'GeneralSurcharge', text: 'General Surcharge' }
    ]
  }),

  mounted() {
    if (this.id) {
      this.ResetForm();
      this.LoadItem(this.id);
    } else {
      this.ResetForm();
    }
  },

  methods: {
    onSourceChange() {
      // Clear fields that are not required for the selected source
      if (this.itemForm.source !== 'UPSDirect') {
        this.itemForm.country = '';
        this.itemForm.chargeClassCode = '';
      }
      
      // Trigger validation
      this.$nextTick(() => {
        this.$validator.validateAll();
      });
    },

    SaveForm() {
      this.savingItem = true;
      this.SaveItem()
        .then(resp => {
          this.ResetForm();
          this.$swal({ title: 'Configuration was saved successfully!', icon: 'success' });
          this.$router.push({
            name: 'Billing.CarrierInvoiceChargeConfigurations.Index'
          });
        })
        .catch(err => {
          this.savingItem = false;
          if (err.response && err.response.data && err.response.data.errorMessage) {
            this.$swal({ title: err.response.data.errorMessage, icon: 'error' });
          } else {
            this.$swal({ title: 'Configuration was NOT saved!', icon: 'error' });
          }
        });
    }
  }
};
</script>